import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiSave, FiVideo, FiImage, FiPlus, FiTrash2 } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';

const ServicesHeroManagement = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  const [heroData, setHeroData] = useState({
    video: '',
    backgroundType: 'color',
    backgroundColor: '#ffffff',
    backgroundImage: null,
    backgroundImageSize: 'cover',
    backgroundImagePosition: 'center',
    backgroundImageRepeat: 'no-repeat',
    title: {
      line1: 'Event',
      line2: 'Planning'
    },
    topSection: {
      enabled: false,
      content: '',
      backgroundColor: '#000000',
      textColor: '#ffffff'
    },
    description: {
      heading: 'The Art of Perfect Planning',
      paragraphs: []
    },
    subtitle: 'Crafting Unforgettable Moments'
  });
  
  const [videoFile, setVideoFile] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [backgroundImageFile, setBackgroundImageFile] = useState(null);
  const [backgroundImagePreview, setBackgroundImagePreview] = useState(null);

  const API_URL = process.env.REACT_APP_API_URL || '/api';

  useEffect(() => {
    fetchHeroData();
  }, []);

  const fetchHeroData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/services-hero`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const result = await response.json();
      
      if (result.success) {
        setHeroData(result.data);
        setVideoPreview(result.data.video);
        setBackgroundImagePreview(result.data.backgroundImage);
      }
    } catch (error) {
      console.error('Error fetching hero data:', error);
      showMessage('error', 'Failed to load hero section data');
    } finally {
      setLoading(false);
    }
  };

  const handleVideoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setVideoFile(file);
      const url = URL.createObjectURL(file);
      setVideoPreview(url);
    }
  };

  const handleBackgroundImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setBackgroundImageFile(file);
      const url = URL.createObjectURL(file);
      setBackgroundImagePreview(url);
    }
  };

  const handleAddParagraph = () => {
    setHeroData({
      ...heroData,
      description: {
        ...heroData.description,
        paragraphs: [...heroData.description.paragraphs, '']
      }
    });
  };

  const handleRemoveParagraph = (index) => {
    setHeroData({
      ...heroData,
      description: {
        ...heroData.description,
        paragraphs: heroData.description.paragraphs.filter((_, i) => i !== index)
      }
    });
  };

  const handleParagraphChange = (index, value) => {
    const newParagraphs = [...heroData.description.paragraphs];
    newParagraphs[index] = value;
    setHeroData({
      ...heroData,
      description: {
        ...heroData.description,
        paragraphs: newParagraphs
      }
    });
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      const formData = new FormData();
      
      // Add video file if changed
      if (videoFile) {
        formData.append('video', videoFile);
      }
      
      // Add background image if changed
      if (backgroundImageFile) {
        formData.append('backgroundImage', backgroundImageFile);
      }
      
      // Add all other fields
      formData.append('backgroundType', heroData.backgroundType);
      formData.append('backgroundColor', heroData.backgroundColor);
      formData.append('backgroundImageSize', heroData.backgroundImageSize);
      formData.append('backgroundImagePosition', heroData.backgroundImagePosition);
      formData.append('backgroundImageRepeat', heroData.backgroundImageRepeat);
      formData.append('titleLine1', heroData.title.line1);
      formData.append('titleLine2', heroData.title.line2);
      formData.append('topSectionEnabled', heroData.topSection.enabled);
      formData.append('topSectionContent', heroData.topSection.content);
      formData.append('topSectionBackgroundColor', heroData.topSection.backgroundColor);
      formData.append('topSectionTextColor', heroData.topSection.textColor);
      formData.append('descriptionHeading', heroData.description.heading);
      formData.append('descriptionParagraphs', JSON.stringify(heroData.description.paragraphs));
      formData.append('subtitle', heroData.subtitle);
      
      const response = await fetch(`${API_URL}/services-hero`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      const result = await response.json();
      
      if (result.success) {
        showMessage('success', 'Hero section updated successfully!');
        fetchHeroData();
        setVideoFile(null);
        setBackgroundImageFile(null);
      } else {
        showMessage('error', result.message || 'Failed to update hero section');
      }
    } catch (error) {
      console.error('Error saving hero data:', error);
      showMessage('error', 'Error saving hero section');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <LoadingContainer>Loading hero section data...</LoadingContainer>;
  }

  return (
    <Container>
      <Header>
        <Title>Services Hero Section</Title>
        <SaveButton onClick={handleSave} disabled={saving}>
          <FiSave /> {saving ? 'Saving...' : 'Save Changes'}
        </SaveButton>
      </Header>

      {message.text && (
        <Message type={message.type}>{message.text}</Message>
      )}

      <Section>
        <SectionTitle>🎬 Background Video</SectionTitle>
        <FileInput>
          <input
            type="file"
            accept="video/*"
            onChange={handleVideoChange}
            id="video-upload"
          />
          <label htmlFor="video-upload">
            <FiVideo /> {videoFile ? videoFile.name : 'Choose video file'}
          </label>
        </FileInput>
        {videoPreview && (
          <VideoPreview>
            <video controls src={videoPreview} />
          </VideoPreview>
        )}
      </Section>

      <Section>
        <SectionTitle>🎨 Background Settings</SectionTitle>
        <FormGroup>
          <Label>Background Type</Label>
          <Select
            value={heroData.backgroundType}
            onChange={(e) => setHeroData({ ...heroData, backgroundType: e.target.value })}
          >
            <option value="color">Solid Color</option>
            <option value="image">Image</option>
          </Select>
        </FormGroup>

        {heroData.backgroundType === 'color' ? (
          <FormGroup>
            <Label>Background Color</Label>
            <ColorInput
              type="color"
              value={heroData.backgroundColor}
              onChange={(e) => setHeroData({ ...heroData, backgroundColor: e.target.value })}
            />
            <ColorValue>{heroData.backgroundColor}</ColorValue>
          </FormGroup>
        ) : (
          <>
            <FileInput>
              <input
                type="file"
                accept="image/*"
                onChange={handleBackgroundImageChange}
                id="bg-image-upload"
              />
              <label htmlFor="bg-image-upload">
                <FiImage /> {backgroundImageFile ? backgroundImageFile.name : 'Choose background image'}
              </label>
            </FileInput>
            {backgroundImagePreview && (
              <ImagePreview>
                <img src={backgroundImagePreview} alt="Background preview" />
              </ImagePreview>
            )}
            
            <FormGroup>
              <Label>Image Size</Label>
              <Select
                value={heroData.backgroundImageSize}
                onChange={(e) => setHeroData({ ...heroData, backgroundImageSize: e.target.value })}
              >
                <option value="cover">Cover (Fill entire area, may crop)</option>
                <option value="contain">Contain (Fit entire image, may show borders)</option>
                <option value="100% 100%">Stretch (Fill 100% width and height)</option>
                <option value="auto">Auto (Original size)</option>
              </Select>
            </FormGroup>

            <FormGroup>
              <Label>Image Position</Label>
              <Select
                value={heroData.backgroundImagePosition}
                onChange={(e) => setHeroData({ ...heroData, backgroundImagePosition: e.target.value })}
              >
                <option value="center">Center</option>
                <option value="top">Top</option>
                <option value="bottom">Bottom</option>
                <option value="left">Left</option>
                <option value="right">Right</option>
                <option value="top left">Top Left</option>
                <option value="top right">Top Right</option>
                <option value="bottom left">Bottom Left</option>
                <option value="bottom right">Bottom Right</option>
              </Select>
            </FormGroup>

            <FormGroup>
              <Label>Image Repeat</Label>
              <Select
                value={heroData.backgroundImageRepeat}
                onChange={(e) => setHeroData({ ...heroData, backgroundImageRepeat: e.target.value })}
              >
                <option value="no-repeat">No Repeat</option>
                <option value="repeat">Repeat Both</option>
                <option value="repeat-x">Repeat Horizontally</option>
                <option value="repeat-y">Repeat Vertically</option>
              </Select>
            </FormGroup>
          </>
        )}
      </Section>

      <Section>
        <SectionTitle>📝 Title Text</SectionTitle>
        <FormGroup>
          <Label>Title Line 1</Label>
          <Input
            type="text"
            value={heroData.title.line1}
            onChange={(e) => setHeroData({
              ...heroData,
              title: { ...heroData.title, line1: e.target.value }
            })}
            placeholder="Event"
          />
        </FormGroup>
        <FormGroup>
          <Label>Title Line 2</Label>
          <Input
            type="text"
            value={heroData.title.line2}
            onChange={(e) => setHeroData({
              ...heroData,
              title: { ...heroData.title, line2: e.target.value }
            })}
            placeholder="Planning"
          />
        </FormGroup>
      </Section>

      <Section>
        <SectionTitle>🔝 Top Section (Above Title)</SectionTitle>
        <CheckboxGroup>
          <Checkbox
            type="checkbox"
            checked={heroData.topSection.enabled}
            onChange={(e) => setHeroData({
              ...heroData,
              topSection: { ...heroData.topSection, enabled: e.target.checked }
            })}
            id="top-section-enabled"
          />
          <CheckboxLabel htmlFor="top-section-enabled">Enable top section</CheckboxLabel>
        </CheckboxGroup>

        {heroData.topSection.enabled && (
          <>
            <FormGroup>
              <Label>Content</Label>
              <TextArea
                value={heroData.topSection.content}
                onChange={(e) => setHeroData({
                  ...heroData,
                  topSection: { ...heroData.topSection, content: e.target.value }
                })}
                placeholder="Enter content for top section..."
                rows="3"
              />
            </FormGroup>
            <FormGroup>
              <Label>Background Color</Label>
              <ColorInput
                type="color"
                value={heroData.topSection.backgroundColor}
                onChange={(e) => setHeroData({
                  ...heroData,
                  topSection: { ...heroData.topSection, backgroundColor: e.target.value }
                })}
              />
              <ColorValue>{heroData.topSection.backgroundColor}</ColorValue>
            </FormGroup>
            <FormGroup>
              <Label>Text Color</Label>
              <ColorInput
                type="color"
                value={heroData.topSection.textColor}
                onChange={(e) => setHeroData({
                  ...heroData,
                  topSection: { ...heroData.topSection, textColor: e.target.value }
                })}
              />
              <ColorValue>{heroData.topSection.textColor}</ColorValue>
            </FormGroup>
          </>
        )}
      </Section>

      <Section>
        <SectionTitle>📄 Description Section</SectionTitle>
        <FormGroup>
          <Label>Heading</Label>
          <Input
            type="text"
            value={heroData.description.heading}
            onChange={(e) => setHeroData({
              ...heroData,
              description: { ...heroData.description, heading: e.target.value }
            })}
            placeholder="The Art of Perfect Planning"
          />
        </FormGroup>

        <FormGroup>
          <Label>Paragraphs</Label>
          {heroData.description.paragraphs.map((para, index) => (
            <ParagraphGroup key={index}>
              <TextArea
                value={para}
                onChange={(e) => handleParagraphChange(index, e.target.value)}
                placeholder={`Paragraph ${index + 1}...`}
                rows="4"
              />
              <RemoveButton onClick={() => handleRemoveParagraph(index)}>
                <FiTrash2 /> Remove
              </RemoveButton>
            </ParagraphGroup>
          ))}
          <AddButton onClick={handleAddParagraph}>
            <FiPlus /> Add Paragraph
          </AddButton>
        </FormGroup>
      </Section>

      <Section>
        <SectionTitle>💫 Subtitle</SectionTitle>
        <FormGroup>
          <Input
            type="text"
            value={heroData.subtitle}
            onChange={(e) => setHeroData({ ...heroData, subtitle: e.target.value })}
            placeholder="Crafting Unforgettable Moments"
          />
        </FormGroup>
      </Section>
    </Container>
  );
};

// Styled Components
const Container = styled.div`
  padding: 32px;
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: #1a1a1a;
`;

const SaveButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: #4285f4;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: #357ae8;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(66, 133, 244, 0.4);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Message = styled.div`
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 24px;
  background: ${props => props.type === 'success' ? '#d4edda' : '#f8d7da'};
  color: ${props => props.type === 'success' ? '#155724' : '#721c24'};
  border: 1px solid ${props => props.type === 'success' ? '#c3e6cb' : '#f5c6cb'};
`;

const Section = styled.div`
  background: white;
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 24px;
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin-bottom: 20px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #555;
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #4285f4;
    box-shadow: 0 0 0 3px rgba(66, 133, 244, 0.1);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #4285f4;
    box-shadow: 0 0 0 3px rgba(66, 133, 244, 0.1);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  background: white;

  &:focus {
    outline: none;
    border-color: #4285f4;
    box-shadow: 0 0 0 3px rgba(66, 133, 244, 0.1);
  }
`;

const ColorInput = styled.input`
  width: 80px;
  height: 40px;
  border: 1px solid #ddd;
  border-radius: 8px;
  cursor: pointer;
  padding: 4px;
`;

const ColorValue = styled.span`
  margin-left: 12px;
  font-family: monospace;
  font-size: 14px;
  color: #666;
`;

const FileInput = styled.div`
  input {
    display: none;
  }

  label {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 12px 20px;
    background: #f5f5f5;
    border: 2px dashed #ddd;
    border-radius: 8px;
    cursor: pointer;
    font-size: 14px;
    color: #666;
    transition: all 0.2s;

    &:hover {
      background: #e8e8e8;
      border-color: #4285f4;
      color: #4285f4;
    }
  }
`;

const VideoPreview = styled.div`
  margin-top: 16px;
  border-radius: 8px;
  overflow: hidden;

  video {
    width: 100%;
    max-height: 300px;
    object-fit: cover;
  }
`;

const ImagePreview = styled.div`
  margin-top: 16px;
  border-radius: 8px;
  overflow: hidden;

  img {
    width: 100%;
    max-height: 300px;
    object-fit: cover;
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
`;

const Checkbox = styled.input`
  width: 20px;
  height: 20px;
  cursor: pointer;
`;

const CheckboxLabel = styled.label`
  font-size: 14px;
  color: #555;
  cursor: pointer;
`;

const ParagraphGroup = styled.div`
  position: relative;
  margin-bottom: 12px;
`;

const RemoveButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 12px;
  background: #ef4444;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;
  margin-top: 8px;
  transition: all 0.2s;

  &:hover {
    background: #dc2626;
  }
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  background: #10b981;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #059669;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 400px;
  font-size: 18px;
  color: #666;
`;

export default ServicesHeroManagement;
